<template>
  <aside
    class="ce-searchFilterBar flex-none"
    :class="{'is-mobile-visible': mobileFilterBarVisible}">
    <slot />
    <button
      @click.prevent="$store.dispatch('hideMobileFilterBar')"
      class="ce-btn ce-searchFilterBar-closeCta">
      <fa-icon icon="times" />
    </button>
    <template
      v-if="groupFilters">
      <filter-group
        v-for="(filters, informationType) in groupedFilterList"
        :key="`filter-group-${informationType}`"
        :information-type="informationType"
        :filters="filters" />
    </template>

    <template v-else>
      <filter-element
        class="ce-searchFilterBar-section"
        v-for="(config, filterCode) in filterList"
        :key="`filter-section-${filterCode}`"
        :filter-code="filterCode"
        :config="config" />
    </template>

    <div
      class="ce-searchFilterBar-resetAllCta ce-searchFilterBar-resetAllCta--desktop"
      v-sticky
      sticky-side="bottom">
      <transition
        name="fade"
        enter-active-class="fadeInUp"
        leave-active-class="fadeOutDown">
        <button
          class="ce-btn ce-btn--small ce-btn-warn"
          :disabled="!hasActiveFilters"
          v-if="hasActiveFilters"
          @click.prevent="$store.dispatch('resetFilters')">
          <fa-layers class="mr3">
            <fa-icon icon="filter" />
            <fa-icon
              icon="minus-circle"
              transform="shrink-7 down-3 right-8" />
          </fa-layers>
          {{ $t('productSearch.products.resetFiltersCta') }}
        </button>
      </transition>
    </div>

    <div class="ce-searchFilterBar-resetAllCta ce-searchFilterBar-resetAllCta--mobile">
      <button
        class="ce-btn ce-btn--small ce-btn-warn"
        :disabled="!hasActiveFilters"
        v-if="hasActiveFilters"
        @click.prevent="$store.dispatch('resetFilters')">
        <fa-layers class="mr3">
          <fa-icon icon="filter" />
          <fa-icon
            icon="minus-circle"
            transform="shrink-7 down-3 right-8" />
        </fa-layers>
        {{ $t('productSearch.products.resetFiltersCta') }}
      </button>
    </div>

    <div class="ce-searchFilterBar-doneCta">
      <button
        @click.prevent="$store.dispatch('hideMobileFilterBar')"
        class="ce-btn ce-btn--fullWidth ce-btn-light">
        {{ $t('filterBar.doneCta') }}
      </button>
    </div>
  </aside>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import FilterGroup from './FilterGroup.vue';
import FilterElement from './filters/FilterElement.vue';

export default {
  props: {
    groupFilters: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  components: { FilterGroup, FilterElement },
  computed: {
    ...mapState(['industries', 'filterConfig', 'mobileFilterBarVisible']),
    ...mapGetters(['urlFilters', 'filters', 'activeIndustries', 'hasActiveFilters']),
    filterList() {
      const filters = this.$cloneObject(this.filters.generic);

      // Add industry specific filters if there is exactly one active industry
      if (this.activeIndustries
        && Array.isArray(this.activeIndustries)
        && this.activeIndustries.length === 1
        && this.filters.hasOwnProperty(this.activeIndustries[0])) {
        Object.assign(filters, this.filters[this.activeIndustries[0]]);
      }

      return filters;
    },
    groupedFilterList() {
      const filterGroups = new Map();

      Object.keys(this.filterList).forEach((filterCode) => {
        const config = this.filterList[filterCode];
        const informationType = (() => {
          if (config.informationType && config.informationType !== 'n/a') {
            return config.informationType;
          }

          return 'generic';
        })();

        if (!filterGroups.has(informationType)) {
          filterGroups.set(informationType, [{
            filterCode,
            config,
            informationType
          }]);
        } else {
          filterGroups.set(informationType, [
            ...filterGroups.get(informationType),
            {
              filterCode,
              config,
              informationType
            }
          ]);
        }
      });

      return Object.fromEntries(filterGroups);
    }
  },
  watch: {
    urlFilters() {
      // Dispatch a scroll event to force a sticky-directive update
      let event;
      if (typeof Event === 'function') {
        event = new Event('scroll');
      } else {
        event = document.createEvent('Event');
        event.initEvent('scroll', true, true);
      }
      window.dispatchEvent(event);
    }
  }
};
</script>

<style lang="less">
  @import (reference) '~styles/includes/base';

  .ce-searchFilterBar {
    width: 300px;
    padding: @space-5 + @space-3 + 40px @space-5 @space-5 0; // top value: SearchField height + main padding + margin searchField <-> content

    .ce-searchFilterBar-resetAllCta {
      z-index: 5 !important;
      display: flex;
      justify-content: center;
      padding-top: @space-4;

      button {
        display: flex;
        align-items: center;
      }

      &.bottom-sticky {
        button {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          box-shadow: 2px -1px 4px fade(@color-mono-20, 30);
        }
      }

      &.ce-searchFilterBar-resetAllCta--desktop {
        display: none;
      }

      @media screen and (min-width: @breakpoint-md) {
        &.ce-searchFilterBar-resetAllCta--desktop {
          display: flex;
        }
        &.ce-searchFilterBar-resetAllCta--mobile {
          display: none;
        }
      }
    }

    .ce-searchFilterBar-group {
      margin-bottom: @space-4;

      .ce-searchFilterBar-groupHeadline {
        font-weight: bold;
        margin-bottom: @space-1;
      }
    }

    .ce-searchFilterBar-section {
      margin-bottom: @space-3;

      .ce-searchFilter-industry, .ce-searchFilter-color {
        margin-bottom: @space-4 + @space-2;
      }

      > div > label {
        display: block;
        margin-bottom: @space-1;
        font-family: @font-helvetica-heavy;
      }
    }

    .ce-searchFilterBar-closeCta {
      display: none;
    }

    .ce-searchFilterBar-doneCta {
      display: none;
    }

    @media screen and (max-width: (@breakpoint-md - 1)) {
      position: fixed;
      left: 0;
      padding: @space-5 @space-4;
      top: 0;
      z-index: 9090;
      background: @color-mono-95;
      height: 100%;
      transition: all 250ms ease-in-out;
      transform: translateX(-100%);
      overflow: auto;

      .ce-searchFilterBar-doneCta {
        display: block;
        margin-top: @space-2;
      }

      &.is-mobile-visible {
        transform: translateX(0);

        .ce-searchFilterBar-closeCta {
          display: block;
          position: absolute;
          top: @space-1;
          right: @space-1;
          color: @color-text-base;
        }
      }
    }

    @media screen and (max-width: (@breakpoint-xxs - 1)) {
      width: 100%;
    }
  }

  body.ce-mobileFilterBarVisible {
    overflow: hidden;
  }
</style>
